import type { ItemType, ShopBootstrap, ShopProduct } from './bootstrap'
import type {
  CandidateContent,
  CatalogueContent,
  CatalogueIdentity,
} from './catalogue'
import { readDisplayMetadata } from './display-metadata'
import { deriveCandidateContent } from './land-skin-art'

// The rules a Classic-style card candidate shares across collections
// (GLOSSARY.md "Hydrated Shop product"): what its item type must carry, and
// whose ownership list judges it. Classic and Daily Picks each derive their
// own candidates and report omissions under their own event; the judgement
// itself lives here once (ADR 0033).

export function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0
}

// Ownership per content kind: tree and land-skin gids are separate
// namespaces, each judged against its own list (GLOSSARY.md "Owned land
// skin").
export type OwnedGids = Record<CatalogueContent, ReadonlySet<number>>

export function deriveOwnedGids(
  source: Pick<ShopBootstrap, 'unlockedLandSkins' | 'unlockedTrees'>,
): OwnedGids {
  const gids = (unlocked: { gid: number }[]) =>
    new Set(unlocked.map(({ gid }) => gid).filter(isPositiveInteger))
  return {
    land_skin: gids(source.unlockedLandSkins),
    tree_type: gids(source.unlockedTrees),
  }
}

export type CardContentFailure = 'invalid_content' | 'missing_required_asset'

export type CardContent = CandidateContent & { gid: number }

// item_contents[0] must be tree_type or land_skin with a positive-integer
// gid, and a land skin must also carry a usable icon URL (GLOSSARY.md
// "Land-skin art"); its display metadata is read for the detail slides and
// is otherwise unread. A failure names the identity when the gid is known.
export function deriveCardContent(
  product: ShopProduct,
  itemType: ItemType | undefined,
):
  | { card: CardContent }
  | { failure: CardContentFailure; identity?: CatalogueIdentity } {
  const content = itemType?.item_contents?.[0]
  if (
    !itemType ||
    !content ||
    (content.type !== 'tree_type' && content.type !== 'land_skin') ||
    !isPositiveInteger(content.value)
  ) {
    return { failure: 'invalid_content' }
  }
  const identity: CatalogueIdentity = {
    content: content.type,
    gid: content.value,
  }
  const candidateContent = deriveCandidateContent(
    identity.content,
    itemType,
    readDisplayMetadata(product.display_metadata),
    product.id,
    identity.gid,
  )
  if (!candidateContent) return { failure: 'missing_required_asset', identity }
  return { card: { ...candidateContent, gid: identity.gid } }
}
